import { Skeleton, Space } from "antd";
import { useReducer, useState } from "react";
import { Action, BudgetReference, createDefaultWorksheet, getSummary, reducer, SaveState, Worksheet } from "./budgetCalculatorModel";
import Calculator from "./Calculator";
import './styles.css'
import BudgetCalculatorContext from "./BudgetCalculatorContext";
import { calculateStatistics } from "./budgetModel";
import WorksheetList from "./WorksheetList";
import { useDebouncedCallback } from '../../helpers/debounce';
import {
  useCopyWorksheetMutation,
  useDeleteWorksheetMutation,
  useSaveWorksheetMutation,
  useWorksheetQuery,
  useWorksheetsInvalidator,
  useWorksheetsQuery
} from "./budgetQueries";

export type Props = {
  budgets: BudgetReference[]
}

export const BudgetCalculator = ({ budgets }: Props) => {
  const [saveState, setSaveState] = useState<SaveState>('SAVED')
  const [selectedWorksheetId, setSelectedWorksheetId] = useState<string | undefined>()
  const [initialTab, setInitialTab] = useState<string | undefined>()
  const [showCongratulations, setShowCongratulations] = useState(false)

  const worksheets = useWorksheetsInvalidator()
  const saveMutation = useSaveWorksheetMutation(() => setSaveState('SAVED'), () => setSaveState('ERROR'))

  const saveNow = (worksheet: Worksheet) => {
    if(!worksheet.isApplied) {
      saveMutation.mutate(worksheet)
      setSaveState('SAVING')
    }
  }
  const debouncedSave = useDebouncedCallback((worksheet: Worksheet) => saveNow(worksheet), 5000)
  const savingReducer = (worksheet: Worksheet, action: Action) => {
    const newWorksheet = reducer(worksheet, action)
    if (action.type === 'WORKSHEET_LOADED') {
      return newWorksheet
    }

    setSaveState('UNSAVED')

    if (action.type === 'WORKSHEET_CREATED') {
      saveNow(newWorksheet)
    }
    else {
      debouncedSave(newWorksheet)
    }

    return newWorksheet
  }
  const [worksheet, dispatch] = useReducer(savingReducer, createDefaultWorksheet())

  const listQuery = useWorksheetsQuery()
  const selectedWorksheetQuery = useWorksheetQuery(selectedWorksheetId, (loaded) => {
    dispatch({ type: 'WORKSHEET_LOADED', payload: loaded })
    setSaveState('SAVED')
  })

  const createWorksheet = (worksheet: Worksheet, showCongratulationsModal = false) => {
    worksheets.cacheWorksheet(worksheet)
    setSelectedWorksheetId(worksheet.budgetWorksheetId)
    setInitialTab('newoverview') // Open to New Overview tab when created from Budget Builder
    setShowCongratulations(showCongratulationsModal)
    dispatch({ type: 'WORKSHEET_CREATED', payload: worksheet })
  }

  const copyMutation = useCopyWorksheetMutation((copy) => {
    setSelectedWorksheetId(copy.budgetWorksheetId)
    dispatch({ type: 'WORKSHEET_LOADED', payload: copy })
    setSaveState('SAVED')
  })

  const deleteMutation = useDeleteWorksheetMutation()

  if (listQuery.isLoading ||
    selectedWorksheetQuery.isLoading ||
    (selectedWorksheetId !== undefined && selectedWorksheetId !== worksheet.budgetWorksheetId)
  ) {
    return <Skeleton active />
  }

  const saveWorksheetNow = async () => {
    debouncedSave.cancel()
    if (saveState !== 'SAVED' && !worksheet.isApplied) {
      await saveMutation.mutateAsync(worksheet).catch(() => undefined)
    }
  }

  const goBackToList = async () => {
    await saveWorksheetNow()

    setSelectedWorksheetId(undefined)
    setInitialTab(undefined) // Reset initial tab when going back to list
    worksheets.invalidateAll()
  }

  if (selectedWorksheetId === undefined) {
    return <Space direction="vertical" size="large">
      <WorksheetList worksheets={listQuery.data!}
        selectWorksheet={setSelectedWorksheetId}
        createWorksheet={createWorksheet}
        copyWorksheet={(worksheetId, title) => copyMutation.mutate({ worksheetId, title })}
        deleteWorksheet={(worksheetId) => deleteMutation.mutate(worksheetId)} />
    </Space>
  }

  const dismissCongratulations = () => {
    setShowCongratulations(false)
  }

  const summary = getSummary(worksheet.data, worksheet.metadata)
  const context = {
    worksheet: worksheet,
    summary,
    statistics: calculateStatistics(summary, worksheet.data.profit),
    budgets,
    saveWorksheetNow,
    dispatch,
    saveState,
    showCongratulations,
    dismissCongratulations
  }
  return <Space direction="vertical" size="large" style={{width: '100%'}}>
    <BudgetCalculatorContext.Provider value={context}>
      <Calculator initialTab={initialTab} goBackToList={goBackToList} />
    </BudgetCalculatorContext.Provider>
  </Space>
}

export default BudgetCalculator
